import { initialState } from './ai-session-context-plugin';
import type { SessionContextAction, SessionContextPluginState } from './types';

function clearCommitResult(state: SessionContextPluginState): SessionContextPluginState {
	if (!('commitActionId' in state) && !('commitResult' in state)) {
		return state;
	}
	const { commitActionId: _commitActionId, commitResult: _commitResult, ...rest } = state;
	return rest;
}

function clearCellKindConversionIntent(
	state: SessionContextPluginState,
): SessionContextPluginState {
	if (!('cellKindConversionIntent' in state)) {
		return state;
	}
	const { cellKindConversionIntent: _cellKindConversionIntent, ...stateWithoutIntent } = state;
	return stateWithoutIntent;
}

function setCellKindConversionIntent(
	state: SessionContextPluginState,
	cellKindConversionIntent: SessionContextPluginState['cellKindConversionIntent'],
): SessionContextPluginState {
	return cellKindConversionIntent !== undefined
		? { ...state, cellKindConversionIntent }
		: clearCellKindConversionIntent(state);
}

/**
 * Reduces AI experience context actions to update the plugin state.
 * Handles actions for resetting, prompt submission, streaming, completion, and errors.
 *
 * @param state - The current AI experience context plugin state
 * @param action - The action to process
 * @returns The updated AI experience context plugin state
 */
export function reducer({
	state,
	action,
}: {
	action: SessionContextAction;
	state: SessionContextPluginState;
}): SessionContextPluginState {
	switch (action.type) {
		case 'RESET':
			return initialState;
		case 'START_SESSION':
			return {
				...clearCommitResult(clearCellKindConversionIntent(state)),
				sessionId: action.sessionId,
				status: 'streaming',
				isCreateSession: action.isCreateSession,
				isChatEditSession: action.isChatEditSession,
				sourceMessageId: action.sourceMessageId,
				isInlineEditSession: action.isInlineEditSession,
				agentAttribution: action.agentAttribution,
			};
		case 'SET_AGENT_ATTRIBUTION':
			return {
				...state,
				agentAttribution: action.agentAttribution,
			};
		case 'PROMPT':
			return {
				...state,
				status: 'prompt',
			};
		case 'STREAM':
			// Prevent streaming if the session is in an error state
			if (state.status === 'error') {
				return { ...clearCommitResult(clearCellKindConversionIntent(state)) };
			}
			// if the previous state was complete it means we are starting a new stream
			const responseVersion = state.status === 'complete' ? 0 : state.responseVersion + 1;
			return setCellKindConversionIntent(
				{
					...clearCommitResult(state),
					adfResponse: action.adfResponse,
					markdown: action.markdown,
					status: 'streaming',
					responseVersion,
				},
				action.cellKindConversionIntent,
			);
		case 'COMPLETE':
			// Prevent completing the session if it is in an error state
			if (state.status === 'error') {
				return { ...clearCommitResult(clearCellKindConversionIntent(state)) };
			}
			return setCellKindConversionIntent(
				{
					...clearCommitResult(state),
					...(action.actionId !== undefined && { commitActionId: action.actionId }),
					adfResponse: action.adfResponse,
					markdown: action.markdown,
					responseTooSimilar: action.responseTooSimilar,
					status: 'complete',
					// If this completion is for a queue-timeout force-finished tool call,
					// remember its id so the next `buildCommitTransaction` persists its
					// positions for resume.
					pendingForceAbortedToolCallId: action.forceAbortedToolCallId,
				},
				action.cellKindConversionIntent,
			);
		case 'ERROR':
			return {
				...clearCommitResult(clearCellKindConversionIntent(state)),
				errorInfo: action.errorInfo,
				status: 'error',
			};
		case 'UPDATE_TRACE_IDS':
			return {
				...state,
				traceIds: action.traceIds,
			};
		case 'UPDATE_ID_MAP':
			return {
				...state,
				idMap: { ...state.idMap, ...action.idMap },
			};
		case 'CANCEL_STREAM':
			// Set the cancelRequested flag so the plugin's view.update() can abort the network request.
			// The subsequent RESET action will clear this flag along with the rest of the state.
			return {
				...state,
				cancelRequested: true,
			};
		case 'PAUSE_STREAM':
			return {
				...state,
				streamPaused: true,
			};
		case 'RESUME_STREAM':
			return {
				...state,
				streamPaused: false,
			};
		case 'SET_HAS_USER_SCROLLED':
			return {
				...state,
				hasUserScrolled: true,
			};
		case 'START_FREE_GEN_STREAM':
			return {
				...state,
				status: 'streaming',
				userInput: action.userInput,
			};
		// [CCI-15904] Ends the AI session. Resets ephemeral stream state (sessionId,
		// status, adfResponse).
		case 'END_SESSION': {
			return {
				...clearCommitResult(clearCellKindConversionIntent(state)),
				sessionId: undefined,
				status: 'idle',
				adfResponse: undefined,
				markdown: undefined,
				cancelRequested: false,
				pendingForceAbortedToolCallId: undefined,
				isCreateSession: undefined,
				isChatEditSession: undefined,
				isInlineEditSession: undefined,
				agentAttribution: undefined,
			};
		}
		// Remove a stored aborted tool call once it has been resumed.
		case 'CLEAR_ABORTED_TOOL_CALL_POSITION': {
			if (!state.abortedToolCallPositions?.[action.toolCallId]) {
				return state;
			}
			const { [action.toolCallId]: _removed, ...rest } = state.abortedToolCallPositions;
			return {
				...state,
				abortedToolCallPositions: rest,
			};
		}
		default:
			return state;
	}
}
